/**
 * src/components/spatial-bindings/useSpatialWrite.ts
 * The transaction lifecycle every binding write shares, and the authority
 * read the buttons are enabled from.
 *
 * Two things here are less obvious than they look.
 *
 * First, `POST /spatial-bindings/submit` polls for inclusion for about
 * forty seconds and then gives up and returns the hash without a ledger.
 * That is not a failure — the transaction is very likely to land — so
 * reporting it as one would teach operators to re-submit writes that are
 * already in flight. It is surfaced as "submitted, confirmation pending"
 * and the chain is re-read instead.
 *
 * Second, the mirror write to GeoLink happens after the chain write and
 * can fail on its own. When it does, the binding is still real. The
 * warning says what is degraded (discoverability) rather than implying the
 * binding did not happen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getSpatialRegistryConfig,
  mirrorObjectOrganization,
  submitSignedSpatialTx,
  type BuiltSpatialTx,
  type ObjectAuthority,
  type SpatialSigner,
} from '../../v2/services/spatialRegistryService';
import { useObjectAuthorization } from '../../hooks/useAuthorization';
import { invalidateAuthorization } from '../../v2/services/authorizationService';

export type TxPhase =
  | 'idle'
  | 'preparing'
  | 'signing'
  | 'submitting'
  | 'mirroring'
  | 'refreshing'
  | 'done'
  | 'failed';

export const TX_PHASE_LABEL: Record<TxPhase, string> = {
  idle: '',
  preparing: 'Preparing transaction…',
  signing: 'Waiting for signature…',
  submitting: 'Submitting and confirming…',
  mirroring: 'Updating GeoLink…',
  refreshing: 'Refreshing on-chain state…',
  done: 'Done',
  failed: 'Failed',
};

export interface TxState {
  phase: TxPhase;
  hash: string | null;
  /** Submitted, but inclusion was never observed before the poll gave up. */
  unconfirmed: boolean;
  notice: string | null;
  warning: string | null;
  error: string | null;
}

const IDLE: TxState = {
  phase: 'idle', hash: null, unconfirmed: false, notice: null, warning: null, error: null,
};

export interface SpatialWriteOptions {
  build: () => Promise<BuiltSpatialTx>;
  successMessage: string;
  /**
   * Write the binding into GeoLink after the chain accepts it. Pass
   * `organizationId: null` to clear the mirror, e.g. on removal.
   */
  mirror?: { objectId: string; organizationId: string | null } | null;
  /** Re-read authoritative state once the write has landed. */
  refresh?: () => Promise<void>;
}

export interface SpatialWriteApi {
  state: TxState;
  /** True while a write is in flight; every trigger must respect it. */
  busy: boolean;
  run: (opts: SpatialWriteOptions) => Promise<boolean>;
  reset: () => void;
}

/**
 * build → sign → submit → mirror → refresh, with one write at a time.
 *
 * The re-entrancy guard is a ref rather than the rendered state because a
 * second click can arrive before React has re-rendered the disabled
 * button, and submitting the same binding twice costs a real fee.
 */
export function useSpatialWrite(
  signer: SpatialSigner | null,
  networkPassphrase?: string,
): SpatialWriteApi {
  const [state, setState] = useState<TxState>(IDLE);
  const inFlight = useRef(false);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const set = useCallback((patch: Partial<TxState>) => {
    if (mounted.current) setState((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => {
    if (mounted.current) setState(IDLE);
  }, []);

  const run = useCallback(async (opts: SpatialWriteOptions): Promise<boolean> => {
    if (inFlight.current) return false;
    if (!signer) {
      setState({ ...IDLE, phase: 'failed', error: 'No wallet is connected to sign this transaction.' });
      return false;
    }

    inFlight.current = true;
    setState({ ...IDLE, phase: 'preparing' });

    try {
      const built = await opts.build();

      set({ phase: 'signing' });
      const signedXdr = await signer.signTransaction(built.xdr, networkPassphrase);

      // Signing and submitting are separate steps here, rather than the
      // combined `signAndSubmitSpatialTx`, only so the operator sees
      // "waiting for signature" and "submitting" as distinct phases.
      set({ phase: 'submitting' });
      const result = await submitSignedSpatialTx(signedXdr);

      const unconfirmed = result.ledger === undefined || result.ledger === null;
      set({ hash: result.hash, unconfirmed });

      // Any binding write can change who may do what, and a child resolves
      // its authority through its ancestors — so a write here can silently
      // change the answer for objects this component has never heard of.
      // The cache is dropped wholesale rather than guessing which entries
      // are affected: re-reading costs a request, honouring a revoked
      // permission costs more.
      invalidateAuthorization();

      let warning: string | null = null;

      if (opts.mirror) {
        set({ phase: 'mirroring' });
        const outcome = await mirrorObjectOrganization(
          opts.mirror.objectId,
          opts.mirror.organizationId,
        );
        if (outcome.outcome === 'skipped' || outcome.outcome === 'failed') {
          warning = outcome.reason;
        }
      }

      if (opts.refresh) {
        set({ phase: 'refreshing' });
        try {
          await opts.refresh();
        } catch {
          // A refresh failure is a display problem, not a write problem.
          warning = warning
            ?? 'The write succeeded but the view could not be refreshed. Reload to see current state.';
        }
      }

      const suffix = unconfirmed
        ? ' Submitted, but confirmation was still pending when we stopped waiting — '
          + 'the state shown may lag by a few seconds.'
        : '';

      set({
        phase: 'done',
        notice: `${opts.successMessage} (tx ${result.hash.slice(0, 8)}…)${suffix}`,
        warning,
      });
      return true;
    } catch (e) {
      set({
        phase: 'failed',
        error: e instanceof Error ? e.message : 'Transaction failed',
      });
      return false;
    } finally {
      inFlight.current = false;
    }
  }, [signer, networkPassphrase, set]);

  const busy = state.phase !== 'idle' && state.phase !== 'done' && state.phase !== 'failed';
  return { state, busy, run, reset };
}

// ── Reads the UI gates on ────────────────────────────────────────────────────

/**
 * Whether the spatial registry is deployed and reachable.
 *
 * `null` while unknown, so a surface can render nothing rather than
 * flashing "not configured" before the first response arrives.
 */
export function useSpatialRegistryConfig(): { configured: boolean | null; contractId: string | null } {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [contractId, setContractId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSpatialRegistryConfig().then((cfg) => {
      if (cancelled) return;
      setConfigured(cfg.configured);
      setContractId(cfg.contractId);
    });
    return () => { cancelled = true; };
  }, []);

  return { configured, contractId };
}

/**
 * What this wallet may actually do to this object, read from the chain.
 *
 * Buttons are disabled from this rather than from which screen the user
 * navigated through. Being in the country admin overlay does not make a
 * wallet the country admin, and the contract will say so at signing time
 * whatever the UI believes.
 *
 * Now a thin adapter over `useObjectAuthorization`, so the binding UI and
 * every other admin surface read the same answer from the same place. The
 * narrow `ObjectAuthority` shape is kept because callers here only ever
 * needed the two operational booleans and the raw grant mask.
 */
export function useObjectAuthority(
  objectId: string | null,
  wallet: string | null,
  /** Bump to re-read after a write. */
  refreshKey: unknown = 0,
): { authority: ObjectAuthority | null; loading: boolean } {
  const { auth, loading, reload } = useObjectAuthorization(objectId, wallet);

  // The old signature invalidated by changing a key rather than by calling
  // a function. Preserved so existing call sites keep working unchanged.
  const seen = useRef(refreshKey);
  useEffect(() => {
    if (seen.current !== refreshKey) {
      seen.current = refreshKey;
      reload();
    }
  }, [refreshKey, reload]);

  const authority = useMemo<ObjectAuthority | null>(() => {
    if (!auth) return null;
    return {
      objectId: auth.objectId,
      objectUuid: auth.objectUuid,
      wallet: auth.wallet,
      canManageObject: auth.canManageObject,
      canManageRules: auth.canManageRules,
      grant: {
        mask: auth.context.grant.mask,
        manageObject: auth.context.grant.manageObject,
        manageRules: auth.context.grant.manageRules,
        manageFunds: auth.context.grant.manageFunds,
      },
    };
  }, [auth]);

  return { authority, loading };
}
